/**
 * @fileoverview Project download split-button.
 *
 * Hidden behind the `projectDownload` feature flag (see {@link IDELayout}).
 *
 * Layout:
 *   ┌──────────────────────┬───┐
 *   │  ⤓  Download         │ ▾ │
 *   └──────────────────────┴───┘
 *      ^ main button         ^ dropdown
 *
 * - Main button → fetches all available `DOWNLOADABLE_ASSETS`, zips them
 *   client-side with JSZip, triggers `{projectName}.zip` browser download.
 * - Dropdown items → one per `DOWNLOADABLE_ASSETS` entry. Unavailable items
 *   are disabled with a "(not generated)" suffix. Clicking an available item
 *   fetches the asset bytes and triggers a single-file download.
 *
 * No version is sent to the server: the API resolves to the project's
 * latest version (FF-gated server-side).
 */

import React, { useState } from "react";
import { Download, ChevronDown, Loader2 } from "lucide-react";
import {
  AdditionalAssetMeta,
  DOWNLOADABLE_ASSETS,
  DownloadAssetMeta,
  TacoAssetType,
} from "@amzn/afxtaco-types";
import {
  bundleFilename,
  sanitizeFilename,
  singleAssetFilename,
} from "@amzn/afxtaco-utilities";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
// Import the pure label util directly (not via the barrel) to avoid pulling
// MarkdownRenderer / react-markdown into this component's module graph.
import { additionalAssetLabel } from "../additionalAssets/additionalAssetLabel";
import {
  getAssetContentUrl,
  getAssetDownloadUrl,
  getProjectPresentation,
} from "../../api/assets/assets";
import { buildAssetZip } from "./buildAssetZip";
import { triggerBrowserDownload } from "./triggerBrowserDownload";

/**
 * Filename for a downloaded additional asset: the project-name prefix (matching
 * the standard-asset convention) plus the asset's own filename.
 * @example "My_Project-cost.md"
 */
function additionalAssetFilename(
  projectName: string | undefined | null,
  asset: AdditionalAssetMeta,
): string {
  return `${sanitizeFilename(projectName)}-${asset.filename}`;
}

interface DownloadProjectButtonProps {
  projectId: string;
  projectName: string;
  /**
   * Set of asset types that are available for this project. Derived from
   * Redux `projectAssets[projectId]` keys. Assets not in this set are shown
   * as disabled in the dropdown with a "(not generated)" suffix.
   */
  availableAssetTypes: ReadonlySet<TacoAssetType>;
  /**
   * Additional (non-canonical) assets for this project — e.g. `cost.md`,
   * `research/notes.md`. Listed in the dropdown after the standard assets and
   * included in the "download all" bundle. Empty when the `additionalAssets`
   * feature isn't enabled or the project has none.
   */
  additionalAssets?: readonly AdditionalAssetMeta[];
  /**
   * Asset types eligible to be listed at all (e.g. gated by a feature flag).
   * Defaults to every {@link DOWNLOADABLE_ASSETS} entry. An asset absent here
   * is hidden entirely — distinct from `availableAssetTypes`, which only
   * controls the generated/disabled state of a listed asset.
   */
  visibleAssetTypes?: ReadonlySet<TacoAssetType>;
  /**
   * Whether the Presentation (HTML) download option is enabled via feature flag.
   * When false, the option is hidden entirely from the dropdown.
   */
  isPresentationEnabled?: boolean;
}

/**
 * Result of trying to fetch one asset for the bundle: either the bytes to zip,
 * or a skip carrying the human-readable label so the caller can tell the user
 * exactly which assets were left out.
 */
type BundleFetchResult =
  | { ok: true; filename: string; data: Blob }
  | { ok: false; label: string };

/**
 * Resolves a single asset to its presigned URL and fetches the bytes.
 *
 * Never throws: on any failure it returns `{ ok: false, label }` so one asset
 * failing doesn't abort the whole bundle, and the caller can report which
 * assets were skipped. The diagram SVG in particular can transiently fail (its
 * render is on demand), so surfacing the skip lets the user retry it.
 */
async function fetchAssetForBundle(
  projectId: string,
  projectName: string,
  meta: DownloadAssetMeta,
): Promise<BundleFetchResult> {
  try {
    const response = await getAssetDownloadUrl({
      projectId,
      assetType: meta.type,
    });
    const presignedUrl = response.data.presignedUrl;
    const fileResponse = await fetch(presignedUrl);
    if (!fileResponse.ok) {
      console.warn(
        `[DownloadProjectButton] Skipping ${meta.type}: HTTP ${fileResponse.status}`,
      );
      return { ok: false, label: meta.label };
    }
    const blob = await fileResponse.blob();
    return {
      ok: true,
      filename: singleAssetFilename(projectName, meta),
      data: blob,
    };
  } catch (err) {
    console.warn(`[DownloadProjectButton] Skipping ${meta.type}:`, err);
    return { ok: false, label: meta.label };
  }
}

/**
 * Path-based sibling of {@link fetchAssetForBundle} for additional assets,
 * which are addressed by relative path (via `getAssetContentUrl`) rather than
 * the `TacoAssetType` enum. Never throws — returns a skip on failure.
 */
async function fetchAdditionalAssetForBundle(
  projectId: string,
  projectName: string,
  asset: AdditionalAssetMeta,
): Promise<BundleFetchResult> {
  const label = additionalAssetLabel(asset.filename);
  try {
    const response = await getAssetContentUrl({ projectId, path: asset.path });
    const fileResponse = await fetch(response.data.presignedUrl);
    if (!fileResponse.ok) {
      console.warn(
        `[DownloadProjectButton] Skipping ${asset.path}: HTTP ${fileResponse.status}`,
      );
      return { ok: false, label };
    }
    const blob = await fileResponse.blob();
    return {
      ok: true,
      filename: additionalAssetFilename(projectName, asset),
      data: blob,
    };
  } catch (err) {
    console.warn(`[DownloadProjectButton] Skipping ${asset.path}:`, err);
    return { ok: false, label };
  }
}

/**
 * Renders the project download split button. Layout matches the design:
 * tooltip on the main button, single-asset items in the dropdown.
 */
export const DownloadProjectButton = ({
  projectId,
  projectName,
  availableAssetTypes,
  additionalAssets = [],
  visibleAssetTypes,
  isPresentationEnabled = false,
}: DownloadProjectButtonProps) => {
  // Assets eligible to be listed (feature-gated). Default: everything.
  const visibleAssets = DOWNLOADABLE_ASSETS.filter(
    (meta) => !visibleAssetTypes || visibleAssetTypes.has(meta.type),
  );
  const [isBundleLoading, setIsBundleLoading] = useState(false);
  const [loadingAssetType, setLoadingAssetType] =
    useState<TacoAssetType | null>(null);
  // Path of the additional asset currently downloading (single-item), if any.
  const [loadingAssetPath, setLoadingAssetPath] = useState<string | null>(null);
  // Whether the HTML presentation is currently being generated/downloaded.
  const [isLoadingPresentation, setIsLoadingPresentation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Non-blocking notice for a partially-successful bundle (some assets zipped,
  // others skipped) — distinct from errorMessage, which is a hard failure.
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);

  const isAnyLoading =
    isBundleLoading ||
    loadingAssetType !== null ||
    loadingAssetPath !== null ||
    isLoadingPresentation;

  // Only downloadable assets that are actually available for this project.
  const availableDownloadableAssets = visibleAssets.filter((meta) =>
    availableAssetTypes.has(meta.type),
  );
  const hasAnyAvailable =
    availableDownloadableAssets.length > 0 || additionalAssets.length > 0;

  const handleBundleDownload = async () => {
    if (isAnyLoading || !hasAnyAvailable) return;
    setIsBundleLoading(true);
    setNoticeMessage(null); // clear any prior partial-success notice
    try {
      const settled = await Promise.all([
        ...availableDownloadableAssets.map((meta) =>
          fetchAssetForBundle(projectId, projectName, meta),
        ),
        ...additionalAssets.map((asset) =>
          fetchAdditionalAssetForBundle(projectId, projectName, asset),
        ),
      ]);
      const entries = settled
        .filter((r): r is { ok: true; filename: string; data: Blob } => r.ok)
        .map(({ filename, data }) => ({ filename, data }));
      const skippedLabels = settled
        .filter((r): r is { ok: false; label: string } => !r.ok)
        .map((r) => r.label);

      if (entries.length === 0) {
        setErrorMessage(
          "No project assets could be downloaded. Please try again after generation completes.",
        );
        return;
      }
      const zipBlob = await buildAssetZip(entries);
      triggerBrowserDownload(zipBlob, bundleFilename(projectName));

      // Partial success: the zip has what worked; tell the user what was left
      // out (and that it may be retryable, e.g. a diagram still rendering).
      if (skippedLabels.length > 0) {
        setNoticeMessage(
          `Downloaded ${entries.length} of ${settled.length} assets. ` +
            `Couldn't include: ${skippedLabels.join(", ")}. ` +
            `These may still be generating — try downloading them individually.`,
        );
      }
    } catch (err) {
      console.error("[DownloadProjectButton] Bundle download failed:", err);
      setErrorMessage("Failed to download project assets. Please try again.");
    } finally {
      setIsBundleLoading(false);
    }
  };

  const handleSingleDownload = async (meta: DownloadAssetMeta) => {
    if (isAnyLoading) return;
    setLoadingAssetType(meta.type);
    try {
      const response = await getAssetDownloadUrl({
        projectId,
        assetType: meta.type,
      });
      const presignedUrl = response.data.presignedUrl;
      // Fetch the bytes from the presigned URL and trigger download from a
      // Blob. We can't use the URL directly with `<a download>` because the
      // S3 presigned URL is cross-origin, and browsers ignore the `download`
      // attribute for cross-origin hrefs — they navigate instead of saving.
      const fileResponse = await fetch(presignedUrl);
      if (!fileResponse.ok) {
        throw new Error(`HTTP ${fileResponse.status}`);
      }
      const blob = await fileResponse.blob();
      triggerBrowserDownload(blob, singleAssetFilename(projectName, meta));
    } catch (err) {
      console.error(
        `[DownloadProjectButton] Failed to download ${meta.type}:`,
        err,
      );
      setErrorMessage(
        `Failed to download ${meta.label}. The asset may not be available yet.`,
      );
    } finally {
      setLoadingAssetType(null);
    }
  };

  const handleSingleAdditionalDownload = async (asset: AdditionalAssetMeta) => {
    if (isAnyLoading) return;
    setLoadingAssetPath(asset.path);
    try {
      const response = await getAssetContentUrl({
        projectId,
        path: asset.path,
      });
      // Same cross-origin reasoning as handleSingleDownload — fetch then save.
      const fileResponse = await fetch(response.data.presignedUrl);
      if (!fileResponse.ok) {
        throw new Error(`HTTP ${fileResponse.status}`);
      }
      const blob = await fileResponse.blob();
      triggerBrowserDownload(blob, additionalAssetFilename(projectName, asset));
    } catch (err) {
      console.error(
        `[DownloadProjectButton] Failed to download ${asset.path}:`,
        err,
      );
      setErrorMessage(
        `Failed to download ${additionalAssetLabel(asset.filename)}. The asset may not be available yet.`,
      );
    } finally {
      setLoadingAssetPath(null);
    }
  };

  const handlePresentationDownload = async () => {
    if (isAnyLoading || !hasAnyAvailable) return;
    setIsLoadingPresentation(true);
    try {
      const html = await getProjectPresentation(projectId);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      triggerBrowserDownload(
        blob,
        `${sanitizeFilename(projectName)}-Presentation.html`,
      );
    } catch (err) {
      console.error(
        "[DownloadProjectButton] Failed to download presentation:",
        err,
      );
      setErrorMessage(
        "Failed to generate the HTML presentation. Please try again.",
      );
    } finally {
      setIsLoadingPresentation(false);
    }
  };

  return (
    <>
      {/* Split button */}
      <div className="inline-flex">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 rounded-r-none border-r-0"
              onClick={handleBundleDownload}
              disabled={isAnyLoading || !hasAnyAvailable}
              aria-label="Download all project assets as a zip"
            >
              {/* The split button is always visible, so it carries the
                  progress signal for ANY in-flight download — including
                  single-item downloads whose dropdown (and its per-item
                  spinner) closes on select. Covers the multi-second wait when
                  the diagram SVG has to render on demand. */}
              {isAnyLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isAnyLoading ? "Preparing…" : "Download"}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {hasAnyAvailable
              ? "Download all project assets as a .zip"
              : "No assets generated yet"}
          </TooltipContent>
        </Tooltip>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="px-2 rounded-l-none"
              disabled={isAnyLoading}
              aria-label="Choose a single asset to download"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {visibleAssets.map((meta) => {
              const isAvailable = availableAssetTypes.has(meta.type);
              const isLoadingThis = loadingAssetType === meta.type;
              return (
                <DropdownMenuItem
                  key={meta.type}
                  onClick={() => isAvailable && handleSingleDownload(meta)}
                  disabled={isAnyLoading || !isAvailable}
                  className={!isAvailable ? "opacity-50" : ""}
                >
                  {isLoadingThis ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {meta.label}
                  {isLoadingThis && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      preparing…
                    </span>
                  )}
                  {!isAvailable && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      not generated
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
            {additionalAssets.map((asset) => {
              const isLoadingThis = loadingAssetPath === asset.path;
              return (
                <DropdownMenuItem
                  key={asset.path}
                  onClick={() => handleSingleAdditionalDownload(asset)}
                  disabled={isAnyLoading}
                >
                  {isLoadingThis ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {additionalAssetLabel(asset.filename)}
                  {isLoadingThis && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      preparing…
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
            {/* Presentation (HTML) — on-demand HTML containing all artifacts */}
            {isPresentationEnabled && (
              <DropdownMenuItem
                onClick={handlePresentationDownload}
                disabled={isAnyLoading || !hasAnyAvailable}
                className={!hasAnyAvailable ? "opacity-50" : ""}
              >
                {isLoadingPresentation ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Presentation (HTML)
                {isLoadingPresentation && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    preparing…
                  </span>
                )}
                {!hasAnyAvailable && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    not generated
                  </span>
                )}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Error dialog — replaces window.alert() */}
      <Dialog
        open={errorMessage !== null}
        onOpenChange={(open) => {
          if (!open) setErrorMessage(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Download Error</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorMessage(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial-success notice — the zip downloaded, but some assets were
          skipped (e.g. a diagram still rendering). Informational, not an error. */}
      <Dialog
        open={noticeMessage !== null}
        onOpenChange={(open) => {
          if (!open) setNoticeMessage(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Some assets weren&apos;t included</DialogTitle>
            <DialogDescription>{noticeMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setNoticeMessage(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DownloadProjectButton;
